import React, { memo, useCallback, useEffect, useState } from 'react';
import * as Yup from 'yup';
import GlobalWrapper from '../../../../components/Wrapper/GlobalWrapper';
import GlobalDialog from '../../../../components/mui/Dialogue/GlobalDialog';
import {
  Box,
  Button,
  Stack,
  TextField,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  FormHelperText,
  CircularProgress,
} from '@mui/material';
import { FormikProvider, Form, useFormik, FieldArray } from 'formik';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AddCircle, RemoveCircle } from '@mui/icons-material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import MotionContainer from '../../../../components/FramerMotion/MotionContainer';
import dayjs from 'dayjs';
import { useDispatch, useSelector } from 'react-redux';
import { holdPaymentUpload } from '../../../../redux/features/generalSlice';
import { useSnackbar } from 'notistack';
import {
  deleteHoldAmt,
  getCartItems,
  getCartItemsSkillx,
  getHoldAmt,
  postHoldAmt,
} from '../../../../redux/features/cartSlice';
import { Icon } from '@iconify/react';
import { useLocation } from 'react-router-dom';

const HoldAmountForm = memo(({ item }) => {
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();

  const { holdAmountData, loading } = useSelector((state) => state.cart);
  const [open, setOpen] = useState(false);

  const validationSchema = Yup.object().shape({
    holdings: Yup.array().of(
      Yup.object().shape({
        holdAmount: Yup.number().required('Hold Amount is required').min(1, 'Hold Amount must be greater than 0'),
        paymentAccountId: Yup.string().required('Payment Account is required'),
        currencyType: Yup.string().required('Currency Type is required'),
        aedAmt: Yup.number().when('currencyType', {
          is: 'AED',
          then: (schema) => schema.required('Amount in AED is required'),
          otherwise: (schema) => schema.notRequired(),
        }),
        exchangeRate: Yup.number().when('currencyType', {
          is: 'AED',
          then: (schema) => schema.required('required'),
          otherwise: (schema) => schema.notRequired(),
        }),
        paymentMode: Yup.string().required('Payment Mode is required'),
        // paymentProof: Yup.mixed().required("Payment Proof is required"),
      })
    ),
  });

  const formik = useFormik({
    initialValues: {
      cartItemId: item.cartId,
      holdings: [
        {
          holdAmount: '',
          paymentDate: dayjs(),
          paymentAccountId: '',
          currencyType: item.selectedCurrency == 1 ? 'INR' : item.selectedCurrency == 3 ? 'AED to INR' : 'AED',
          paymentMode: '',
          paymentProof: '',
          aedAmt: '',
          exchangeRate: 23,
          screenshot: '',
        },
      ],
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      const validation = values.holdings.some((item) => item.screenshot === '');

      if (validation) {
        enqueueSnackbar('Please upload all payment proofs', {
          variant: 'error',
        });
        return;
      }

      dispatch(postHoldAmt(values)).then((res) => {
        if (res.payload.status === 'success') {
          enqueueSnackbar('Hold Amount submitted successfully', {
            variant: 'success',
          });
          const leadId = location?.state?.leadId;
          dispatch(getCartItemsSkillx(leadId));
          setOpen(false);
        } else {
          enqueueSnackbar('Hold Amount submission failed', {
            variant: 'error',
          });
        }
      });
    },
  });

  useEffect(() => {
    if (open) {
      dispatch(getHoldAmt(item.cartId));
    }
  }, [open, dispatch, item.cartId]);

  useEffect(() => {
    if (holdAmountData.length > 0) {
      setFieldValue('holdings', holdAmountData);
    }
  }, [holdAmountData]);

  const { values, handleChange, handleSubmit, setFieldValue } = formik;
  const handleAddMore = useCallback(
    (push) => () => {
      push({
        holdAmount: '',
        paymentDate: dayjs().format('YYYY-MM-DD'),
        paymentAccountId: '',
        currencyType: item.selectedCurrency == 1 ? 'INR' : item.selectedCurrency == 3 ? 'AED to INR' : 'AED',
        paymentProof: '',
        paymentMode: '',
        aedAmt: '',
        exchangeRate: 23,
        screenshot: '',
      });
    },
    []
  );
  const paymentMethods = [
    { id: '1', name: 'Cash In hand' },
    { id: '2', name: 'Bank Transfer' },
    { id: '3', name: 'Stripe' },
  ];

  const handleFileChange = (event, setFieldValue, index) => {
    const file = event.target.files[0];
    if (file) {
      const fileType = file.type;
      const fileSize = file.size / 1024 / 1024;

      if (fileType === 'application/pdf' && fileSize > 2) {
        alert('PDF file must be less than 2MB');
        return;
      }

      if (
        (fileType === 'image/jpeg' ||
          fileType === 'image/jpg' ||
          fileType === 'image/png' ||
          fileType === 'image/webp') &&
        fileSize > 1
      ) {
        alert('Image file must be less than 1MB');
        return;
      }
      dispatch(holdPaymentUpload(file)).then((res) => {
        if (res.payload.status === 'success') {
          setFieldValue(`holdings[${index}].screenshot`, res.payload.data.filename);
          const previewUrl = fileType.startsWith('image/') ? URL.createObjectURL(file) : '';
          setFieldValue(`holdings[${index}].paymentProof`, {
            file,
            previewUrl,
          });
          enqueueSnackbar('Screenshot uploaded successfully..', {
            variant: 'success',
          });
        } else {
          enqueueSnackbar('Screenshot upload failed..', {
            variant: 'error',
          });
        }
      });
    }
  };

  const handleRemove = useCallback(
    (remove, holding, index) => () => {
      if (holding?.id) {
        dispatch(deleteHoldAmt(holding?.id)).then((res) => {
          if (res.payload.status === 'success') {
            enqueueSnackbar('Hold Amount deleted successfully', {
              variant: 'success',
            });
            const leadId = location?.state?.leadId;
            dispatch(getCartItemsSkillx(leadId));
            remove(index);
          }
        });
      } else {
        enqueueSnackbar('Hold Amount deleted successfully', {
          variant: 'success',
        });
        remove(index);
      }
    },
    []
  );

  return (
    <GlobalDialog
      title="Hold Amount"
      open={open}
      setOpen={setOpen}
      closeButton={true}
      fullWidth
      maxWidth="md"
      popupButton={
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Button variant="outlined" size="small" onClick={() => setOpen(true)}>
            <Icon icon="stash:hand-holding-dollar" width="24" height="24" />
            Hold Amount
          </Button>
        </Box>
      }
      content={
        <GlobalWrapper>
          {open && (
            <FormikProvider value={formik}>
              <Form onSubmit={handleSubmit}>
                <FieldArray name="holdings">
                  {({ push, remove }) => (
                    <Stack spacing={3}>
                      {values.holdings.map((holding, index) => (
                        <Box
                          key={index}
                          sx={{
                            border: '1px solid #ccc',
                            p: 2,
                            borderRadius: 2,
                          }}
                        >
                          <Stack direction="column" spacing={2} alignItems="center">
                            <MotionContainer delay={0.6}>
                              <TextField
                                fullWidth
                                name={`holdings[${index}].holdAmount`}
                                label="Hold Amount"
                                type="number"
                                value={holding.holdAmount}
                                onChange={(e) => {
                                  const value = parseFloat(e.target.value) || 0;
                                  formik.setFieldValue(`holdings[${index}].holdAmount`, value);
                                  formik.setFieldValue(`holdings[${index}].aedAmt`, value * 23);
                                }}
                                onWheel={(e) => e.target.blur()}
                                inputProps={{
                                  inputMode: 'decimal',
                                  pattern: '[0-9]*[.]?[0-9]+',
                                  onWheel: (e) => e.target.blur(),
                                }}
                                error={
                                  formik.touched.holdings?.[index]?.holdAmount &&
                                  Boolean(formik.errors.holdings?.[index]?.holdAmount)
                                }
                                helperText={
                                  formik.touched.holdings?.[index]?.holdAmount &&
                                  formik.errors.holdings?.[index]?.holdAmount
                                }
                              />
                            </MotionContainer>
                            <MotionContainer delay={0.6}>
                              <FormControl
                                fullWidth
                                error={
                                  formik.touched.holdings?.[index]?.paymentMode &&
                                  Boolean(formik.errors.holdings?.[index]?.paymentMode)
                                }
                              >
                                <InputLabel id={`payment-mode-label-${index}`}>Payment Mode</InputLabel>
                                <Select
                                  name={`holdings[${index}].paymentMode`}
                                  labelId={`payment-mode-label-${index}`}
                                  id={`payment-mode-select-${index}`}
                                  value={holding.paymentMode}
                                  label="Payment Mode"
                                  onChange={(event) =>
                                    setFieldValue(`holdings[${index}].paymentMode`, parseInt(event.target.value))
                                  }
                                >
                                  {paymentMethods.map((item) => (
                                    <MenuItem key={item.id} value={item.id}>
                                      {item.name}
                                    </MenuItem>
                                  ))}
                                </Select>

                                {formik.touched.holdings?.[index]?.paymentMode &&
                                  formik.errors.holdings?.[index]?.paymentMode && (
                                    <FormHelperText>{formik.errors.holdings[index].paymentMode}</FormHelperText>
                                  )}
                              </FormControl>
                            </MotionContainer>
                            <MotionContainer delay={0.6}>
                              <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                  label="Payment Date"
                                  value={dayjs(holding.paymentDate)}
                                  onChange={(date) =>
                                    setFieldValue(`holdings[${index}].paymentDate`, dayjs(date).format('YYYY-MM-DD'))
                                  }
                                  slotProps={{
                                    textField: { fullWidth: true },
                                  }}
                                />
                              </LocalizationProvider>
                            </MotionContainer>
                            <MotionContainer delay={0.6}>
                              <FormControl
                                fullWidth
                                error={
                                  formik.touched.holdings?.[index]?.paymentAccountId &&
                                  Boolean(formik.errors.holdings?.[index]?.paymentAccountId)
                                }
                              >
                                <InputLabel id="demo-simple-select-label">Payment Account</InputLabel>
                                <Select
                                  name={`holdings[${index}].paymentAccountId`}
                                  labelId="demo-simple-select-label"
                                  id="demo-simple-select"
                                  value={holding.paymentAccountId}
                                  label="Payment Account"
                                  onChange={(event) =>
                                    setFieldValue(`holdings[${index}].paymentAccountId`, event.target.value)
                                  }
                                >
                                  {[
                                    { id: 1, value: 'Indian A/c' },
                                    { id: 2, value: 'GCC A/c' },
                                  ].map((item) => (
                                    <MenuItem key={item.id} value={item.id}>
                                      {item.value}
                                    </MenuItem>
                                  ))}
                                </Select>
                                {formik.touched.holdings?.[index]?.paymentAccountId &&
                                  formik.errors.holdings?.[index]?.paymentAccountId && (
                                    <FormHelperText>{formik.errors.holdings[index].paymentAccountId}</FormHelperText>
                                  )}
                              </FormControl>
                            </MotionContainer>
                            <MotionContainer delay={0.6}>
                              <FormControl
                                fullWidth
                                error={
                                  formik.touched.holdings?.[index]?.currencyType &&
                                  Boolean(formik.errors.holdings?.[index]?.currencyType)
                                }
                              >
                                <InputLabel id="demo-simple-select-label">Currency</InputLabel>
                                <Select
                                  name={`holdings[${index}].currencyType`}
                                  labelId="demo-simple-select-label"
                                  id="demo-simple-select"
                                  value={holding.currencyType}
                                  label="Currency"
                                  disabled
                                  onChange={(event) =>
                                    setFieldValue(`holdings[${index}].currencyType`, event.target.value)
                                  }
                                >
                                  {[
                                    { id: 'INR', name: 'INR' },
                                    { id: 'AED', name: 'AED' },
                                    { id: 'AED to INR', name: 'AED to INR' },
                                  ].map((item) => (
                                    <MenuItem key={item.id} value={item.id}>
                                      {item.name}
                                    </MenuItem>
                                  ))}
                                </Select>
                                {formik.touched.holdings?.[index]?.currencyType &&
                                  formik.errors.holdings?.[index]?.currencyType && (
                                    <FormHelperText>{formik.errors.holdings[index].currencyType}</FormHelperText>
                                  )}
                              </FormControl>
                            </MotionContainer>
                            {formik.values.holdings[index].currencyType === 'AED' ? (
                              <>
                                <MotionContainer delay={0.6}>
                                  <TextField
                                    fullWidth
                                    name={`holdings[${index}].exchangeRate`}
                                    label="Enter INR amount for 1 AED"
                                    type="number"
                                    disabled
                                    // ={
                                    //   formik.values.holdings[index].id
                                    //     ? true
                                    //     : false
                                    // }
                                    value={holding.exchangeRate}
                                    onChange={(e) => {
                                      const inrForAed = parseFloat(e.target.value) || '';
                                      const holdAmount = parseFloat(formik.values.holdings[index].holdAmount) || 0;
                                      const aedAmt = inrForAed * holdAmount;

                                      formik.setFieldValue(`holdings[${index}].exchangeRate`, inrForAed);
                                      formik.setFieldValue(`holdings[${index}].aedAmt`, aedAmt);
                                    }}
                                    onWheel={(e) => e.target.blur()}
                                    inputProps={{
                                      inputMode: 'decimal',
                                      pattern: '[0-9]*[.]?[0-9]+',
                                      onWheel: (e) => e.target.blur(),
                                    }}
                                    error={
                                      formik.touched.holdings?.[index]?.exchangeRate &&
                                      Boolean(formik.errors.holdings?.[index]?.exchangeRate)
                                    }
                                    helperText={
                                      formik.touched.holdings?.[index]?.exchangeRate &&
                                      formik.errors.holdings?.[index]?.exchangeRate
                                    }
                                  />
                                </MotionContainer>
                                <MotionContainer delay={0.6}>
                                  <TextField
                                    fullWidth
                                    name={`holdings[${index}].aedAmt`}
                                    label="Aed Amount"
                                    type="number"
                                    disabled
                                    value={holding.aedAmt}
                                    onChange={handleChange}
                                    onWheel={(e) => e.target.blur()}
                                    inputProps={{
                                      inputMode: 'decimal',
                                      pattern: '[0-9]*[.]?[0-9]+',
                                      onWheel: (e) => e.target.blur(),
                                    }}
                                    error={
                                      formik.touched.holdings?.[index]?.aedAmt &&
                                      Boolean(formik.errors.holdings?.[index]?.aedAmt)
                                    }
                                    helperText={
                                      formik.touched.holdings?.[index]?.aedAmt &&
                                      formik.errors.holdings?.[index]?.aedAmt
                                    }
                                  />{' '}
                                </MotionContainer>
                              </>
                            ) : null}

                            {/* ------------------------------------------------------------- */}
                            <MotionContainer delay={0.6}>
                              <Box>
                                <Button disabled={loading} variant="outlined" component="label">
                                  {loading ? <CircularProgress size="30px" /> : 'Upload Payment Proof'}
                                  {loading ? 'please wait...' : ''}

                                  <input
                                    type="file"
                                    accept="image/jpeg, image/jpg, image/png, image/webp, application/pdf"
                                    hidden
                                    onChange={(event) => handleFileChange(event, setFieldValue, index)}
                                  />
                                </Button>

                                {holding?.id || holding?.paymentProof ? (
                                  <Box>
                                    {holding?.paymentProof?.previewUrl || holding?.screenshot ? (
                                      <Box>
                                        <img
                                          src={
                                            holding?.paymentProof?.previewUrl ||
                                            `https://protest.teaminterval.net/${holding.screenshot}`
                                          }
                                          alt="Payment Proof Preview"
                                          style={{
                                            width: '150px',
                                            height: 'auto',
                                            borderRadius: '8px',
                                            marginTop: '8px',
                                          }}
                                        />
                                      </Box>
                                    ) : null}
                                  </Box>
                                ) : null}
                                <Typography variant="body2" mt={1}>
                                  {holding?.paymentProof?.file?.name || holding?.screenshot}
                                </Typography>
                              </Box>
                            </MotionContainer>

                            {/* {values.holdings.length > 1 && ( */}
                            <IconButton color="error" onClick={handleRemove(remove, holding, index)}>
                              <RemoveCircle />
                            </IconButton>
                            {/* )} */}
                          </Stack>
                        </Box>
                      ))}
                      <Button variant="outlined" startIcon={<AddCircle />} onClick={handleAddMore(push)}>
                        Add More
                      </Button>

                      <Button disabled={loading} type="submit" variant="contained" color="primary">
                        {loading ? <CircularProgress size="30px" /> : 'Submit'}

                        {loading ? 'please wait...' : ''}
                      </Button>
                    </Stack>
                  )}
                </FieldArray>
              </Form>
            </FormikProvider>
          )}
        </GlobalWrapper>
      }
    />
  );
});

export default HoldAmountForm;
